import React, { createContext, useContext, useId, useState } from "react";
import { Icon } from "@iconify/react";
import KpiDetailsModal from "./KpiDetailsModal";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  PALETTE,
  downloadCsv,
  formatBucket,
  formatDate,
  formatNumber,
  formatValue,
  getStatusClass,
  initials,
} from "./reportUtils";

/* ------------------------------ layout ------------------------------ */
export const Row = ({ layout = "1-1", children }) => (
  <div className={`rp-row rp-row-${layout}`}>{children}</div>
);

// Scrolls to a panel and flashes it so the user sees where the details are. Returns false if it is not on the page.
export const focusPanel = (id) => {
  const el =
    typeof document !== "undefined" ? document.getElementById(id) : null;
  if (!el) return false;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.remove("rp-flash");
  void el.offsetWidth; // restart the animation if it is clicked twice
  el.classList.add("rp-flash");
  window.setTimeout(() => el.classList.remove("rp-flash"), 2600);
  return true;
};

// Works out a table from a chart's own data, so every chart can be flipped to a table.
const buildAutoTable = (el) => {
  const p = el.props;
  const t = el.type;
  if (t === TrendChart) {
    return {
      columns: [
        cols.text("label", "Date"),
        ...(p.series || []).map((s) =>
          cols.num(s.key, s.label, s.unit, p.currency),
        ),
      ],
      rows: (p.data || []).map((r) => ({
        ...r,
        label: formatBucket(r.bucket ?? r.date ?? r.month, p.granularity),
      })),
    };
  }
  if (t === DonutChart || t === BarList || t === ColumnChart) {
    const data = p.data || [];
    const total = data.reduce((sum, d) => sum + d.value, 0);
    const showShare = !["PERCENT", "DAYS", "HOURS", "DECIMAL"].includes(
      String(p.unit || "").toUpperCase(),
    );
    return {
      columns: [
        cols.text("name", "Name"),
        cols.num("value", "Total", p.unit, p.currency),
        ...(showShare
          ? [
              {
                key: "share",
                header: "Share",
                align: "right",
                render: (v) => `${v}%`,
              },
            ]
          : []),
      ],
      rows: data.map((d) => ({
        ...d,
        share: total ? Math.round((d.value / total) * 100) : 0,
      })),
    };
  }
  if (t === Funnel) {
    const st = p.stages || [];
    return {
      columns: [
        cols.text("name", "Stage"),
        cols.num("count", "Count"),
        ...(st.some((x) => x.value != null)
          ? [cols.num("value", "Money", "CURRENCY", p.currency)]
          : []),
        ...(st.some((x) => x.conversion != null)
          ? [cols.num("conversion", "From last stage", "PERCENT")]
          : []),
        ...(st.some((x) => x.days != null)
          ? [cols.num("days", "Days in stage", "DECIMAL")]
          : []),
      ],
      rows: st,
    };
  }
  if (t === Leaderboard) {
    const rows = p.rows || [];
    const labels = [];
    rows.forEach((r) =>
      (r.metrics || []).forEach((m) => {
        if (!labels.find((l) => l.label === m.label))
          labels.push({ label: m.label, unit: m.unit });
      }),
    );
    return {
      columns: [
        cols.num("rank", "#"),
        cols.text("name", "Name"),
        cols.num(
          "primary",
          p.primaryLabel
            ? String(p.primaryLabel).replace(/^./, (c) => c.toUpperCase())
            : "Total",
          p.primaryUnit,
          p.currency,
        ),
        ...labels.map((l, i) => cols.num(`m${i}`, l.label, l.unit, p.currency)),
      ],
      rows: rows.map((r, i) => ({
        rank: i + 1,
        name: r.name,
        primary: r.primary,
        ...Object.fromEntries(
          (r.metrics || []).map((m) => [
            `m${labels.findIndex((l) => l.label === m.label)}`,
            m.value,
          ]),
        ),
      })),
    };
  }
  return null;
};

const findAutoTable = (children) => {
  const types = [
    DonutChart,
    BarList,
    ColumnChart,
    TrendChart,
    Funnel,
    Leaderboard,
  ];
  const el = React.Children.toArray(children).find(
    (k) => React.isValidElement(k) && types.includes(k.type),
  );
  const built = el ? buildAutoTable(el) : null;
  return built && built.rows.length ? built : null;
};

export const Panel = ({
  id,
  title,
  subtitle,
  actions,
  children,
  className = "",
  flush,
  table,
}) => {
  const [mode, setMode] = useState("chart");
  const auto = table === false ? null : findAutoTable(children);
  const showTable = !!auto && mode === "table";
  const toggle = auto ? (
    <div
      className="rp-mini-switch"
      role="group"
      aria-label="Show as chart or table"
    >
      <button
        type="button"
        aria-pressed={mode === "chart"}
        title="Show as chart"
        onClick={() => setMode("chart")}
      >
        <Icon icon="mdi:chart-areaspline" />
      </button>
      <button
        type="button"
        aria-pressed={mode === "table"}
        title="Show as table"
        onClick={() => setMode("table")}
      >
        <Icon icon="mdi:table" />
      </button>
    </div>
  ) : null;
  return (
    <section id={id} className={`rp-panel ${className}`}>
      {(title || actions || toggle) && (
        <header className="rp-panel-head">
          <div>
            <h3>{title}</h3>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {(actions || toggle) && (
            <div className="rp-panel-actions">
              {actions}
              {toggle}
            </div>
          )}
        </header>
      )}
      <div className={`rp-panel-body ${flush ? "rp-flush" : ""}`}>
        {showTable ? (
          <DataTable columns={auto.columns} rows={auto.rows} maxHeight={320} />
        ) : (
          children
        )}
      </div>
    </section>
  );
};

export const Empty = ({ text = "No data for this selection" }) => (
  <div className="rp-empty-inline">
    <Icon icon="mdi:chart-box-outline" />
    <span>{text}</span>
  </div>
);

export const ErrorState = ({ message, onRetry }) => (
  <div className="rp-error" role="alert">
    <Icon icon="mdi:alert-circle-outline" />
    <div>
      <strong>{message}</strong>
      <p>Check your connection or the selected dates, then try again.</p>
    </div>
    {onRetry && (
      <button type="button" onClick={onRetry}>
        Retry
      </button>
    )}
  </div>
);

export const ReportSkeleton = () => (
  <div className="rp-skeleton" aria-busy="true" aria-label="Loading report">
    <div className="rp-skeleton-kpis">
      {Array.from({ length: 8 }).map((_, i) => (
        <span key={i} />
      ))}
    </div>
    <div className="rp-skeleton-row">
      <span />
      <span />
    </div>
    <div className="rp-skeleton-row">
      <span />
      <span />
    </div>
  </div>
);

export const AlertStrip = ({ items }) => {
  const visible = (items || []).filter(Boolean);
  if (!visible.length) return null;
  return (
    <div className="rp-alerts">
      {visible.map((a) => (
        <div key={a.text} className={`rp-alert rp-alert-${a.tone || "warn"}`}>
          <Icon icon={a.icon || "mdi:alert-outline"} />
          <span>{a.text}</span>
        </div>
      ))}
    </div>
  );
};

export const StatusBadge = ({ value }) => (
  <span className={getStatusClass(value)}>{value || "—"}</span>
);

export const Avatar = ({ name, src, size = 34 }) =>
  src ? (
    <img
      className="rp-avatar"
      src={src}
      alt=""
      style={{ width: size, height: size }}
    />
  ) : (
    <span
      className="rp-avatar rp-avatar-initials"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials(name)}
    </span>
  );

/* ------------------------- page-wide settings ------------------------- */
// view: "charts" | "table";  periodText: "this month";  compareWord: "before" | "last year"
export const ReportContext = createContext({
  view: "charts",
  periodText: "in this period",
  compareWord: "before",
});
export const useReportContext = () => useContext(ReportContext);

/* -------------------------------- KPI -------------------------------- */
// Compact card: icon, then name + short plain-English description, then the number on the right.
export const KpiCard = ({ kpi, currency, color, onOpen }) => {
  const { periodText, compareWord } = useReportContext();
  const good = kpi.goodDirection === "DOWN" ? "DOWN" : "UP";
  const hasChange = kpi.changePct !== null && kpi.changePct !== undefined;
  const pct = hasChange ? Math.round(Math.abs(kpi.changePct)) : 0;
  const tone =
    !hasChange || pct === 0 || kpi.trend === "FLAT"
      ? "flat"
      : kpi.trend === good
        ? "good"
        : "bad";
  const desc = (kpi.desc || "").replace("{p}", periodText);
  const value = formatValue(kpi.value, kpi.unit, currency);
  const change = !hasChange
    ? null
    : pct === 0
      ? `Same as ${compareWord}`
      : `${pct}% ${kpi.changePct > 0 ? "more" : "less"} than ${compareWord}`;
  const was =
    kpi.previousValue !== null && kpi.previousValue !== undefined
      ? ` (${compareWord}: ${formatValue(kpi.previousValue, kpi.unit, currency)})`
      : "";
  const clickProps = onOpen
    ? {
        role: "button",
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen();
          }
        },
      }
    : {};
  return (
    <article
      className={`rp-kpi${onOpen ? " is-clickable" : ""}`}
      style={{ "--accent": color }}
      title={`${kpi.label}: ${value}${desc ? `. ${desc}` : ""}${change ? `. ${change}${was}` : ""}${onOpen ? ". Click to see the details." : ""}`}
      {...clickProps}
    >
      <span className="rp-kpi-icon">
        <Icon icon={kpi.icon || "mdi:chart-box-outline"} />
      </span>
      <div className="rp-kpi-text">
        <span className="rp-kpi-label">{kpi.label}</span>
        {desc && <span className="rp-kpi-desc">{desc}</span>}
        {change && (
          <span className={`rp-kpi-change is-${tone}`}>
            <Icon
              icon={
                tone === "flat"
                  ? "mdi:minus"
                  : kpi.changePct > 0
                    ? "mdi:arrow-up"
                    : "mdi:arrow-down"
              }
            />
            {change}
          </span>
        )}
      </div>
      <div className="rp-kpi-value">{value}</div>
      {onOpen && <Icon className="rp-kpi-go" icon="mdi:table-search" />}
    </article>
  );
};

/**
 * details[kpi.key] = { scrollTo?: "panel-id", modal?: {...} }
 *  - scrollTo: if that panel is on the page right now, scroll to it and highlight it.
 *  - modal: otherwise open a pop-up with the table behind the number (see KpiDetailsModal).
 */
export const KpiGrid = ({ kpis, currency, details }) => {
  const [openKey, setOpenKey] = useState(null);
  // "new_clients", "NEW-CLIENTS" and "newClients" all count as the same key.
  const norm = (v) =>
    String(v)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  const lookup = Object.fromEntries(
    Object.entries(details || {}).map(([key, value]) => [norm(key), value]),
  );
  const detailFor = (k) => details?.[k.key] || lookup[norm(k.key)];
  const canOpen = (k) => !!(detailFor(k)?.modal || detailFor(k)?.scrollTo);
  const open = (k) => {
    const d = detailFor(k);
    if (!d) return;
    if (d.scrollTo && focusPanel(d.scrollTo)) return;
    if (d.modal) setOpenKey(k.key);
  };
  const active = openKey ? kpis.find((k) => k.key === openKey) : null;
  return (
    <>
      <div className="rp-kpis">
        {kpis.map((k, i) => (
          <KpiCard
            key={k.key}
            kpi={k}
            currency={currency}
            color={PALETTE[i % PALETTE.length]}
            onOpen={canOpen(k) ? () => open(k) : undefined}
          />
        ))}
      </div>
      {active && detailFor(active)?.modal && (
        <KpiDetailsModal
          kpi={active}
          detail={detailFor(active)}
          currency={currency}
          onClose={() => setOpenKey(null)}
        />
      )}
    </>
  );
};

/* ------------------------------- tooltips ------------------------------- */
const ChartTooltip = ({ active, payload, label, series, currency, unit }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rp-tooltip">
      <div className="rp-tooltip-title">
        {label ?? payload[0]?.payload?.name}
      </div>
      {payload.map((p) => {
        const s = series?.find((x) => x.key === p.dataKey);
        return (
          <div className="rp-tooltip-row" key={p.dataKey || p.name}>
            <i style={{ background: p.color || p.stroke || p.fill }} />
            <span>{s?.label || p.name}</span>
            <b>{formatValue(p.value, s?.unit || unit || "COUNT", currency)}</b>
          </div>
        );
      })}
    </div>
  );
};

/* ------------------------------ trend chart ------------------------------ */
// series: [{ key, label, color, type: "area" | "bar" | "line", unit, axis: "right" }]
export const TrendChart = ({
  data = [],
  series,
  granularity,
  height = 270,
  currency,
}) => {
  const uid = useId().replace(/:/g, "");
  const rows = data.map((r) => ({
    ...r,
    label: formatBucket(r.bucket ?? r.date ?? r.month, granularity),
  }));
  if (!rows.length) return <Empty />;
  const hasRight = series.some((s) => s.axis === "right");
  return (
    <div className="rp-chart" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 8, right: hasRight ? 4 : 8, left: -10, bottom: 0 }}
        >
          <defs>
            {series.map((s, i) => (
              <linearGradient
                key={s.key}
                id={`${uid}${i}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="0%" stopColor={s.color} stopOpacity={0.28} />
                <stop offset="100%" stopColor={s.color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--rp-border)"
          />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "var(--rp-muted)" }}
            tickLine={false}
            axisLine={false}
            minTickGap={18}
          />
          <YAxis
            yAxisId="left"
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "var(--rp-muted)" }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => formatValue(v, series[0]?.unit, currency)}
            width={52}
          />
          {hasRight && (
            <YAxis
              yAxisId="right"
              orientation="right"
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "var(--rp-muted)" }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
          )}
          <Tooltip
            content={<ChartTooltip series={series} currency={currency} />}
            cursor={{ stroke: "var(--rp-border)" }}
          />
          {series.map((s, i) => {
            const common = {
              key: s.key,
              dataKey: s.key,
              name: s.label,
              yAxisId: s.axis === "right" ? "right" : "left",
              isAnimationActive: false,
            };
            if (s.type === "bar")
              return (
                <Bar
                  {...common}
                  fill={s.color}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={26}
                />
              );
            if (s.type === "line")
              return (
                <Line
                  {...common}
                  type="monotone"
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                />
              );
            return (
              <Area
                {...common}
                type="monotone"
                stroke={s.color}
                strokeWidth={2}
                fill={`url(#${uid}${i})`}
              />
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SeriesLegend = ({ series }) => (
  <div className="rp-legend">
    {series.map((s) => (
      <span key={s.key}>
        <i style={{ background: s.color }} />
        {s.label}
      </span>
    ))}
  </div>
);

/* ------------------------------ column chart ------------------------------ */
export const ColumnChart = ({
  data = [],
  color = PALETTE[0],
  height = 230,
  unit,
  currency,
}) => {
  if (!data.length) return <Empty />;
  return (
    <div className="rp-chart" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 8, right: 8, left: -14, bottom: 0 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="var(--rp-border)"
          />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "var(--rp-muted)" }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: "var(--rp-muted)" }}
            tickLine={false}
            axisLine={false}
          />
          <Tooltip
            content={
              <ChartTooltip
                unit={unit}
                currency={currency}
                series={[{ key: "value", label: "Total", unit }]}
              />
            }
            cursor={{ fill: "var(--rp-surface-2)" }}
          />
          <Bar
            dataKey="value"
            fill={color}
            radius={[5, 5, 0, 0]}
            maxBarSize={44}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/* ------------------------------- donut chart ------------------------------- */
export const DonutChart = ({
  data = [],
  unit,
  currency,
  onSelect,
  centerLabel = "Total",
  height = 190,
}) => {
  const items = data.filter((d) => d.value > 0);
  const total = items.reduce((s, d) => s + d.value, 0);
  if (!items.length) return <Empty />;
  return (
    <div className="rp-donut">
      <div className="rp-donut-chart" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={items}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="92%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
              onClick={onSelect ? (d) => onSelect(d.key ?? d.name) : undefined}
            >
              {items.map((d, i) => (
                <Cell
                  key={d.name}
                  fill={PALETTE[i % PALETTE.length]}
                  cursor={onSelect ? "pointer" : "default"}
                />
              ))}
            </Pie>
            <Tooltip
              content={
                <ChartTooltip
                  unit={unit}
                  currency={currency}
                  series={[{ key: "value", label: "Total", unit }]}
                />
              }
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="rp-donut-center">
          <b>{formatValue(total, unit, currency)}</b>
          <span>{centerLabel}</span>
        </div>
      </div>
      <ul className="rp-donut-legend">
        {items.slice(0, 8).map((d, i) => (
          <li key={d.name}>
            <button
              type="button"
              disabled={!onSelect}
              onClick={() => onSelect?.(d.key ?? d.name)}
            >
              <i style={{ background: PALETTE[i % PALETTE.length] }} />
              <span>{d.name}</span>
              <em>{formatValue(d.value, unit, currency)}</em>
              <small>{formatNumber((d.value / total) * 100, 0)}%</small>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ------------------------------ horizontal bars ------------------------------ */
export const BarList = ({
  data = [],
  color = PALETTE[0],
  unit,
  currency,
  onSelect,
  limit = 8,
  max,
}) => {
  const items = data.slice(0, limit);
  if (!items.length) return <Empty />;
  const top = max ?? Math.max(...items.map((d) => d.value), 1);
  return (
    <ul className="rp-barlist">
      {items.map((d) => (
        <li key={d.name}>
          <button
            type="button"
            disabled={!onSelect}
            onClick={() => onSelect?.(d.key ?? d.name)}
          >
            <div className="rp-barlist-head">
              <span>{d.name}</span>
              <b>{formatValue(d.value, unit, currency)}</b>
            </div>
            <div className="rp-barlist-track">
              <div
                style={{
                  width: `${Math.max((d.value / top) * 100, 2)}%`,
                  background: color,
                }}
              />
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

/* ---------------------------------- funnel ---------------------------------- */
// stages: [{ name, count, value, conversion, days }]
export const Funnel = ({ stages = [], currency }) => {
  if (!stages.length) return <Empty />;
  const top = Math.max(...stages.map((s) => s.count), 1);
  return (
    <ol className="rp-funnel">
      {stages.map((s, i) => (
        <li key={`${s.name}-${i}`}>
          <div
            className="rp-funnel-bar"
            style={{
              width: `${Math.max((s.count / top) * 100, 8)}%`,
              background: PALETTE[i % PALETTE.length],
            }}
          >
            <span>{s.name}</span>
            <b>{formatNumber(s.count)}</b>
          </div>
          <div className="rp-funnel-meta">
            {s.value !== undefined && s.value !== null && (
              <span>{formatValue(s.value, "CURRENCY", currency)}</span>
            )}
            {s.conversion !== undefined && s.conversion !== null && (
              <span>{formatNumber(s.conversion, 0)}% of previous</span>
            )}
            {s.days !== undefined && s.days !== null && (
              <span>{formatNumber(s.days, 1)}d avg in stage</span>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

/* -------------------------------- leaderboard -------------------------------- */
// rows: [{ name, avatarUrl, primary, metrics: [{ label, value, unit }] }]
export const Leaderboard = ({
  rows = [],
  primaryUnit,
  primaryLabel,
  currency,
  limit = 8,
}) => {
  const items = rows.slice(0, limit);
  if (!items.length) return <Empty text="No ranking data yet" />;
  const top = Math.max(...items.map((r) => r.primary), 1);
  return (
    <ol className="rp-leaderboard">
      {items.map((r, i) => (
        <li key={`${r.name}-${i}`} className={i === 0 ? "rp-leader-first" : ""}>
          <span className="rp-rank">{i + 1}</span>
          <Avatar name={r.name} src={r.avatarUrl} />
          <div className="rp-leader-main">
            <div className="rp-leader-line">
              <strong>{r.name}</strong>
              <b>
                {formatValue(r.primary, primaryUnit, currency)}
                <small>{primaryLabel}</small>
              </b>
            </div>
            <div className="rp-leader-track">
              <div
                style={{ width: `${Math.max((r.primary / top) * 100, 3)}%` }}
              />
            </div>
            {!!r.metrics?.length && (
              <div className="rp-leader-metrics">
                {r.metrics
                  .filter((m) => m.value !== null && m.value !== undefined)
                  .map((m) => (
                    <span key={m.label}>
                      {m.label} <b>{formatValue(m.value, m.unit, currency)}</b>
                    </span>
                  ))}
              </div>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

/* ---------------------------------- lists ---------------------------------- */
// items: [{ id, title, subtitle, right, badge, icon }]
export const ItemList = ({
  items = [],
  empty = "Nothing to show",
  onSelect,
}) => {
  if (!items.length) return <Empty text={empty} />;
  return (
    <ul className="rp-items">
      {items.map((it, i) => (
        <li key={it.id ?? i}>
          <button
            type="button"
            disabled={!onSelect}
            onClick={() => onSelect?.(it)}
          >
            <Avatar name={it.title} size={32} />
            <div className="rp-item-main">
              <strong>{it.title}</strong>
              {it.subtitle && <span>{it.subtitle}</span>}
            </div>
            <div className="rp-item-side">
              {it.badge && <StatusBadge value={it.badge} />}
              {it.right && <small>{it.right}</small>}
            </div>
          </button>
        </li>
      ))}
    </ul>
  );
};

/* ---------------------------------- heatmap ---------------------------------- */
export const Heatmap = ({ matrix }) => {
  const rowKeys = Object.keys(matrix || {});
  const colKeys = [
    ...new Set(rowKeys.flatMap((r) => Object.keys(matrix[r] || {}))),
  ];
  if (!rowKeys.length || !colKeys.length) return <Empty />;
  const max = Math.max(
    ...rowKeys.flatMap((r) => colKeys.map((c) => Number(matrix[r]?.[c]) || 0)),
    1,
  );
  return (
    <div className="rp-heatmap-wrap">
      <table className="rp-heatmap">
        <thead>
          <tr>
            <th />
            {colKeys.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((r) => (
            <tr key={r}>
              <th>{r}</th>
              {colKeys.map((c) => {
                const v = Number(matrix[r]?.[c]) || 0;
                return (
                  <td
                    key={c}
                    style={{
                      background: v
                        ? `rgba(1,123,254,${0.1 + (v / max) * 0.75})`
                        : "transparent",
                      color: v / max > 0.55 ? "#fff" : "inherit",
                    }}
                  >
                    {v || ""}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ------------------------------ table helpers ------------------------------ */
export const cols = {
  text: (key, header, render) => ({
    key,
    header,
    ...(render ? { render } : {}),
  }),
  num: (key, header, unit, currency) => ({
    key,
    header,
    align: "right",
    render: (v) =>
      v === null || v === undefined || v === ""
        ? "—"
        : formatValue(v, unit, currency),
  }),
  date: (key, header, time) => ({
    key,
    header,
    render: (v) => formatDate(v, time),
  }),
  badge: (key, header) => ({
    key,
    header,
    render: (v) => <StatusBadge value={v} />,
  }),
};

export const DataTable = ({
  columns,
  rows = [],
  empty = "Nothing to show",
  maxHeight = 340,
}) => {
  if (!rows.length) return <Empty text={empty} />;
  return (
    <div className="rp-datatable" style={{ maxHeight }}>
      <table className="rp-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ textAlign: c.align || "left" }}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c.key} style={{ textAlign: c.align || "left" }}>
                  {c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const TablePanel = ({
  id,
  title,
  subtitle,
  columns,
  rows,
  exportName,
  empty,
}) => (
  <Panel
    id={id}
    title={title}
    subtitle={subtitle}
    flush
    className="rp-tablecard"
    actions={
      rows?.length ? (
        <button
          type="button"
          className="rp-btn rp-btn-sm"
          onClick={() => downloadCsv(exportName || title, columns, rows)}
        >
          <Icon icon="mdi:download-outline" /> CSV
        </button>
      ) : null
    }
  >
    <DataTable columns={columns} rows={rows || []} empty={empty} />
  </Panel>
);

// A chart's data as a simple table: name, amount, share of total.
export const DistTable = ({
  title,
  data = [],
  nameHeader = "Name",
  valueHeader = "Total",
  unit,
  currency,
  share = true,
}) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const columns = [
    cols.text("name", nameHeader),
    cols.num("value", valueHeader, unit, currency),
    ...(share
      ? [
          {
            key: "share",
            header: "Share",
            align: "right",
            render: (v) => `${v}%`,
          },
        ]
      : []),
  ];
  const rows = data.map((d) => ({
    ...d,
    share: total ? Math.round((d.value / total) * 100) : 0,
  }));
  return (
    <TablePanel
      title={title}
      columns={columns}
      rows={rows}
      exportName={title}
    />
  );
};

// A time chart's data as a table: one row per date.
export const TrendTable = ({
  title,
  data = [],
  series,
  granularity,
  currency,
}) => {
  const columns = [
    cols.text("label", "Date"),
    ...series.map((s) => cols.num(s.key, s.label, s.unit, currency)),
  ];
  const rows = data.map((r) => ({
    ...r,
    label: formatBucket(r.bucket ?? r.date ?? r.month, granularity),
  }));
  return (
    <TablePanel
      title={title}
      columns={columns}
      rows={rows}
      exportName={title}
    />
  );
};
